'use client';

/**
 * @fileoverview Client hook for managing chat session list metadata via
 * the /api/projects/:id/chat/sessions endpoints.
 *
 * Exposes a tight CRUD surface: list, rename, delete, load-one. Handles
 * optimistic UI for renames and deletes (common cases that shouldn't
 * block the user on a round-trip) with rollback on failure.
 */

import { useCallback, useEffect, useState } from 'react';
import { authFetch } from '@/lib/auth-fetch';
import { useToast } from '@/hooks/use-toast';
import type { ChatSession } from '@/domains/chat/schemas';
import type { ChatSessionSummary } from '@/domains/chat/services';

interface UseChatSessionsResult {
  sessions: ChatSessionSummary[];
  isLoading: boolean;
  refresh: () => Promise<void>;
  loadSession: (sessionId: string) => Promise<ChatSession | null>;
  renameSession: (sessionId: string, title: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  /** Inserts a new summary locally after a session is created server-side. */
  upsertLocalSummary: (summary: ChatSessionSummary) => void;
  /** Updates messageCount + updatedAt for a session after a turn completes. */
  bumpLocalSummary: (sessionId: string, deltaMessages: number) => void;
}

export function useChatSessions(projectId: string): UseChatSessionsResult {
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    try {
      const res = await authFetch(`/api/projects/${projectId}/chat/sessions`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { sessions: ChatSessionSummary[] };
      setSessions(data.sessions);
    } catch (error) {
      console.error('[useChatSessions] Failed to list sessions:', error);
      toast({
        variant: 'destructive',
        title: 'Could not load chat history',
        description: (error as Error).message,
      });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, toast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loadSession = useCallback(
    async (sessionId: string): Promise<ChatSession | null> => {
      try {
        const res = await authFetch(
          `/api/projects/${projectId}/chat/sessions/${sessionId}`,
        );
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { session: ChatSession };
        return data.session;
      } catch (error) {
        console.error('[useChatSessions] Failed to load session:', error);
        toast({
          variant: 'destructive',
          title: 'Could not open chat',
          description: (error as Error).message,
        });
        return null;
      }
    },
    [projectId, toast],
  );

  const renameSession = useCallback(
    async (sessionId: string, title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;

      // Optimistic update
      const previous = sessions;
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? { ...s, title: trimmed, updatedAt: new Date().toISOString() }
            : s,
        ),
      );

      try {
        const res = await authFetch(
          `/api/projects/${projectId}/chat/sessions/${sessionId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: trimmed }),
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch (error) {
        setSessions(previous);
        toast({
          variant: 'destructive',
          title: 'Rename failed',
          description: (error as Error).message,
        });
      }
    },
    [projectId, sessions, toast],
  );

  const deleteSession = useCallback(
    async (sessionId: string) => {
      const previous = sessions;
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));

      try {
        const res = await authFetch(
          `/api/projects/${projectId}/chat/sessions/${sessionId}`,
          { method: 'DELETE' },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch (error) {
        setSessions(previous);
        toast({
          variant: 'destructive',
          title: 'Delete failed',
          description: (error as Error).message,
        });
      }
    },
    [projectId, sessions, toast],
  );

  const upsertLocalSummary = useCallback((summary: ChatSessionSummary) => {
    setSessions((prev) => {
      const without = prev.filter((s) => s.id !== summary.id);
      return [summary, ...without].sort((a, b) =>
        (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''),
      );
    });
  }, []);

  const bumpLocalSummary = useCallback(
    (sessionId: string, deltaMessages: number) => {
      const now = new Date().toISOString();
      setSessions((prev) => {
        const next = prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                messageCount: s.messageCount + deltaMessages,
                updatedAt: now,
              }
            : s,
        );
        return next.sort((a, b) =>
          (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''),
        );
      });
    },
    [],
  );

  return {
    sessions,
    isLoading,
    refresh,
    loadSession,
    renameSession,
    deleteSession,
    upsertLocalSummary,
    bumpLocalSummary,
  };
}
